import { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FORM_MODE } from "../../config/constants";
import Modal from "../Shared/Modal";
import AvailabilityForm from "./AvailabilityForm";
import { saveAvailability } from "../../api/availabilityApi";
import { useToast } from "../../hooks/useToast";

// Layered quick-create modal for availability, opened from the services-page
// ATTENTION STRIP so the provider can add a missing availability without
// leaving the page. Renders as its own Dialog (z-[60]); Headless UI scopes
// ESC/backdrop to the topmost dialog.
//
// IMPORTANT — only mount this where NO other Modal/Dialog is already open (the
// services page, not inside the service drawer). Stacking it over the service
// drawer froze the inner form and misrouted its submit to the service form —
// which is exactly why ServiceForm's own "not bookable" warnings use an
// in-drawer STEP SWAP (see ServiceForm's availStep) instead of this component.
// If you need in-drawer availability creation, reuse that step-swap pattern;
// do not open this modal from within another Dialog.
const QuickAvailabilityModal = ({ open, onClose, tabGroup }) => {
  const [isFormDirty, setIsFormDirty] = useState(false);
  const modalCloseRef = useRef(null);
  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutateAsync: mutateSaveAvailability } = useMutation({
    mutationFn: ({ data }) => saveAvailability(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["availabilities"], exact: true });
      queryClient.invalidateQueries({ queryKey: ["setupProgress"], exact: true });
      toast.success(
        "Availability Created",
        "New availability has been created successfully"
      );
      onClose();
    },
  });

  // CREATE mode never reaches the update/patch paths; stubs satisfy the form's
  // prop contract.
  const noop = async () => {};

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={() => {
        setIsFormDirty(false);
        onClose();
      }}
      title="Add Availability"
      size="lg"
      className="z-[60]"
      confirmClose={isFormDirty}
      closeRef={modalCloseRef}
    >
      <AvailabilityForm
        activeTabGroup={tabGroup}
        closeDrawerHandler={onClose}
        saveAvailability={mutateSaveAvailability}
        patchAvailability={noop}
        updateAvailability={noop}
        defaultAvailability={null}
        mode={FORM_MODE.CREATE}
        onCancelHandler={() => modalCloseRef.current?.()}
        setIsFormDirty={setIsFormDirty}
        lockMeetMode={true}
      />
    </Modal>
  );
};

export default QuickAvailabilityModal;
